<template>
    <Portal to="modal">
        <ModalContainer
            key="conceptBYOFeedbackSurveyModal"
            :is-dark-mode="isDarkMode"
            :show-close-button="true"
            @close="emitClose"
        >
            <div class="concept-byo-feedback-survey">
                <div class="concept-byo-feedback-survey__title-section" v-dark="isDarkMode">
                    <IconTarget
                        class="concept-byo-feedback-survey__title-icon"
                        v-dark
                    />
                    <div class="concept-byo-feedback-survey__title">
                        Interested in concepts?
                    </div>
                    <div class="concept-byo-feedback-survey__text" v-dark>
                        We're in the early stages of developing this feature, and would love your feedback.
                    </div>
                </div>
                <div class="concept-byo-feedback-survey__form">
                    <PocketTextarea
                        autoFocus="true"
                        v-model="otherConcepts"
                        label="What other concepts do you want to study?"
                        class="concept-byo-feedback-survey__field concept-byo-feedback-survey__field-input"
                        :is-dark-mode="isDarkMode"
                        height="62"
                        width=""
                        @keydown.enter="submitForm"
                    />
                    <PocketTextarea
                        v-model="whyThoseConcepts"
                        label="Why those concepts?"
                        class="concept-byo-feedback-survey__field concept-byo-feedback-survey__field-input"
                        :is-dark-mode="isDarkMode"
                        height="62"
                        width=""
                        @keydown.enter="submitForm"
                    />
                </div>
                <div class="concept-byo-feedback-survey__actions">
                    <PocketButton
                        class="concept-byo-feedback-survey__dismiss-btn"
                        :is-dark-mode="isDarkMode"
                        @click="emitClose"
                        type="secondary"
                    >
                        Cancel
                    </PocketButton>
                    <PocketButton
                        class="concept-byo-feedback-survey__submit-btn"
                        :disabled="isSubmitDisabled"
                        :is-dark-mode="isDarkMode"
                        @click="submitForm"
                    >
                        Submit
                    </PocketButton>
                </div>
            </div>
        </ModalContainer>
    </Portal>
</template>

<script lang="ts">
import { Component, Emit, Prop, Vue } from 'vue-facing-decorator'
import UIKit from '@pocketprep/ui-kit'
import { userModule } from '@/store/user/module'
import { analyticsModule } from '@/store/analytics/module'
import IconTarget from '@/components/ConceptQuizExperiment/IconTarget.vue'
import { examMetadataModule } from '@/store/examMetadata/module'

@Component({
    components: {
        ModalContainer: UIKit.ModalContainer,
        PocketButton: UIKit.Button,
        Modal: UIKit.Modal,
        PocketTextarea: UIKit.Textarea,
        IconTarget,
    },
})
export default class ConceptBYOSurvey extends Vue {
    @Prop() selectedConcept?: string

    otherConcepts = ''
    whyThoseConcepts = ''
    conceptBYOConfig: {
        examMetadataId: string
        concepts?: string[]
    } | null = null

    get isDarkMode () {
        return userModule.state.settings.isDarkMode
    }

    get isSubmitDisabled () {
        return !this.otherConcepts && !this.whyThoseConcepts
    }

    get examMetadata () {
        return examMetadataModule.getters.getCurrentExamMetadata()
    }

    get currentExamMetadata () {
        return examMetadataModule.getters.getCurrentExamMetadata()
    }

    get concepts () {
        return this.conceptBYOConfig?.concepts || []
    }

    async mounted () {
        // once the survey is seen, we dont want to show the concept BYO experiment to the user again
        localStorage.setItem('conceptBYOExperiment_hasSeenSurvey', 'true')
        
        const variant = await analyticsModule.actions.amplitudeFetchVariant('concept-byo')
        this.conceptBYOConfig = ((variant?.payload || []) as NonNullable<typeof this.conceptBYOConfig>[]).find(c =>
            c.examMetadataId === this.currentExamMetadata?.objectId
        ) || null
        const seenExperimentCount = Number(localStorage.getItem('conceptBYOExperiment_experimentSeenCount')) || 0

        analyticsModule.actions.amplitudeTrack('Concept BYO Concept Selected', {
            conceptBYOVariant: this.concepts.length ? 'list' : 'search',
            conceptBYOVariantSeenCount: seenExperimentCount,
            examGuid: this.currentExamMetadata?.examGuid || '',
            conceptBYOSelectedConcept: this.selectedConcept,
        })
    }

    submitForm () {
        const seenExperimentCount = Number(localStorage.getItem('conceptBYOExperiment_experimentSeenCount')) || 0
        analyticsModule.actions.amplitudeTrack('Concept BYO Survey Answered', {
            conceptBYOVariant: this.concepts.length ? 'list' : 'search',
            conceptBYOOtherConcepts: this.otherConcepts,
            conceptBYOWhyThoseConcepts: this.whyThoseConcepts,
            examGuid: this.examMetadata?.examGuid || '',
            conceptBYOSelectedConcept: this.selectedConcept,
            conceptBYOVariantSeenCount: seenExperimentCount,
        })
        this.emitSubmitSurvey()
    }

    @Emit('submitSurvey')
    emitSubmitSurvey () {
        return true
    }

    @Emit('close')
    emitClose () {
        return true
    }
}
</script>

<style lang="scss" scoped>
.concept-byo-feedback-survey {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 520px;
    box-sizing: border-box;
    
    @include breakpoint(black-bear) {
        width: 100%;
        margin: 0 auto;
    }

    &__title-section {
        width: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 24px 32px 0;
        box-sizing: border-box;
        border-bottom: 1px solid $fog;

        @include breakpoint(black-bear) {
            padding: 24px 32px 0 32px;
        }

        &--dark {
            border-color: $ash;
        }
    }

    &__title-icon {
        width: 18px;
        height: 18px;
        margin-bottom: 8px;
        color: $ash;
    }

    &__title {
        font-weight: 600;
        font-size: 18px;
        line-height: 25px;
        margin-bottom: 6px;
        letter-spacing: 0.3px;

        &--explanation {
            margin-bottom: 30px;
        }

        @include breakpoint(black-bear) {
            width: 110%;
            text-align: center;
        }
    }

    &__text {
        font-size: 15px;
        line-height: 20px;
        color: $ash;
        margin: 0 auto 30px;
        text-align: center;
        max-width: 376px;

        &--dark {
            color: $fog;
        }
    }

    &__form {
        margin-top: 30px;
        padding: 0 40px;
        box-sizing: border-box;
        width: 100%;
    }

    &__modal {
        left: 50%;
        position: absolute;
        transform: translateX(-50%);
        top: 94px;
    }

    &__field {
        margin-bottom: 20px;
    }

    &__field :deep(.uikit-textarea__label) {
        color: $brand-black;
        margin-left: 0;
        font-size: 15px;
        font-weight: 600;
        line-height: 20px;

        @include breakpoint(black-bear) {
            padding-right: 0;
        }

        &.uikit-radio__label--dark,
        &.uikit-textarea__label--dark {
            color: $white;
        }
    }

    &__field-input :deep(.uikit-textarea__textarea) {
        margin-left: -8px;
        margin-right: -8px;
        width: calc(100% + 16px);
        height: 62px;
        display: block;
        resize: none;

        @include breakpoint(black-bear) {
            height: 72px;
        }
    }

    &__actions {
        margin-top: 10px;
        align-self: center;
        padding: 0 40px 32px;
    }

    &__submit-btn {
        margin-left: 8px;
    }
}
</style>